<template>
  <div>
    <v-date-time-picker class="mt-3"
                        v-model="field"
                        icon="calendar_today"
                        :title="'Set ' + title"
                        :minDate="minDate"
                        persistent
    >
      <template v-slot:field="{ on }">
        <v-layout wrap>
          <v-flex xs5>
            <p class="subheading font-weight-bold mt-2">
              {{ title }}
            </p>
          </v-flex>
          <v-flex xs7>
            <v-text-field class="mt-0 pt-0"
                          append-icon="event"
                          :value="`${dateLabel}`"
                          readonly
                          hide-details
                          v-on="on"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </template>
      <template v-slot:instructions>
        <p class="title mb-2 d-block">{{ instructions }}</p>
      </template>
    </v-date-time-picker>
  </div>
</template>

<script>
import moment from 'moment'
import ModelBinding from '@/mixins/ModelBinding'
import { VDateTimePicker } from '@/components/DateTimePicker'
import { timezones, getDefaultTimezoneIndex, getTimezoneIndex, datetimeForHumans } from '@/services/time'

export default {
  name: 'DateField',
  mixins: [ ModelBinding ],
  components: {
    VDateTimePicker
  },
  props: {
    location: String,
    title: String,
    instructions: String,
    defaultOffset: Object
  },
  data () {
    return {
      timezones,
      field: {
        date: null,
        time: '00:00',
        timezone: getDefaultTimezoneIndex()
      }
    }
  },
  computed: {
    /**
     * Display login due date for users
     */
    date () {
      return moment.tz(`${this.field.date} ${this.field.time}`, this.selectedTimezone.name)
    },
    /**
     * Display due date for users
     */
    dateLabel () {
      if (this.model === null) {
        return 'Anytime'
      }
      return this.date.format(datetimeForHumans) + ' - ' + this.selectedTimezone.label
    },
    /**
     * Due Date converted to UTC
     */
    utcDate () {
      return moment.tz(this.date.format('YYYY-MM-DD HH:mm'), this.selectedTimezone.name)
    },
    /**
     * Min due date
     */
    minDate () {
      return moment.tz(moment(), this.selectedTimezone.name).format('YYYY-MM-DD')
    },
    selectedTimezone () {
      return this.timezones[this.field.timezone]
    }
  },
  watch: {
    /**
     * Re-populate timezone if user location changed
     */
    location: {
      handler: 'populateField',
      immediate: true
    },
    /**
     * Update model binding value when changing any field value
     */
    field: {
      handler: 'changeValue',
      immediate: true,
      deep: true
    }
  },
  methods: {
    /**
     * Change value of model binding
     */
    changeValue () {
      if (this.field.date !== null) {
        this.model = this.utcDate.toISOString()
      } else {
        this.model = null
      }
    },
    /**
     * Populate due date field value from provided or default login window
     */
    populateField () {
      let field = null
      if (this.model !== null) {
        field = this.convertDate(this.model)
      } else if (this.defaultOffset) {
        field = this.convertDate(moment().add(this.defaultOffset))
      }

      if (field) {
        this.$set(this, 'field', field)
      }
    },
    /**
     * Default login window value
     */
    convertDate (date) {
      const timezone = this.timezoneIndexFromLocation()
      const datetime = moment.tz(date, this.timezones[timezone].name)

      return {
        date: datetime.format('YYYY-MM-DD'),
        time: datetime.format('HH:mm'),
        timezone: timezone
      }
    },
    /**
     * Get timezone for the provided location if exists
     */
    timezoneIndexFromLocation () {
      const location = (this.location || '').replace(' ', '_')

      return this.field.timezone === null
        ? getTimezoneIndex(location, false) || getDefaultTimezoneIndex()
        : getTimezoneIndex(location, false) || this.field.timezone
    }
  }
}
</script>
